<main>
  <loading-overlay [isLoading]="isLoading()"></loading-overlay>

  <!-- Header -->
  <header>
    <div class="headline">
      <h1 *ngIf="!isCreateMode">{{ 'Inventory_Adjustment_Details_Title' | translateWF: 'Inventory Adjustment Details' }}: {{ adjustment?.number || '' }}</h1>
      <h1 *ngIf="isCreateMode && !isLotItemMode && !isLotReductionMode">{{ 'Create_Inventory_Adjustment' | translateWF: 'Create Inventory Adjustment' }}</h1>
      <h1 *ngIf="isCreateMode && isLotItemMode">{{ 'Create_Lot_Item' | translateWF: 'Create Lot Item' }}</h1>
      <h1 *ngIf="isCreateMode && isLotReductionMode">
        {{
          'Create_Lot_Reduction'
            | translateWF
              : 'Create Lot-Based
        Reduction'
        }}
      </h1>
    </div>
    <div class="buttons">
      <!-- View Mode Buttons -->
      <p-button *ngIf="!isCreateMode && canVoid()" [label]="'Void_Adjustment' | translateWF: 'Void Adjustment'" icon="pi pi-times" severity="danger" (click)="voidAdjustment()" />
      <span class="separator" *ngIf="!isCreateMode && canVoid()"></span>
      <p-button *ngIf="!isCreateMode" [label]="'Back_To_List' | translateWF: 'Back to List'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />

      <!-- Standard Adjustment Create Mode Buttons -->
      <p-button
        *ngIf="isCreateMode && !isLotItemMode && !isLotReductionMode"
        [label]="'Create_Adjustment' | translateWF: 'Create Adjustment'"
        icon="pi pi-check"
        [disabled]="!isFormValid"
        [loading]="isLoading()"
        (click)="submit()"
      />

      <!-- Lot Item Create Mode Buttons -->
      <p-button
        *ngIf="isCreateMode && isLotItemMode"
        [label]="'Create_Lot_Item' | translateWF: 'Create Lot Item'"
        icon="pi pi-check"
        [disabled]="!isLotItemFormValid"
        [loading]="isLoading()"
        (click)="submitLotItem()"
      />

      <!-- Lot Reduction Create Mode Buttons -->
      <p-button
        *ngIf="isCreateMode && isLotReductionMode"
        [label]="'Create_Reduction' | translateWF: 'Create Reduction'"
        icon="pi pi-check"
        [disabled]="!isLotReductionFormValid"
        [loading]="isLoading()"
        (click)="submitLotReduction()"
      />

      <p-button *ngIf="isCreateMode" [label]="'Common_Cancel' | translateWF: 'Cancel'" icon="pi pi-times" variant="outlined" (click)="cancel()" />
    </div>
  </header>

  <!-- LOT-BASED REDUCTION MODE -->
  <div class="content col" *ngIf="isCreateMode && isLotReductionMode">
    <form [formGroup]="lotReductionForm" class="col">
      <p-panel [header]="'Lot_Based_Reduction_Info' | translateWF: 'Lot-Based Reduction Information'">
        <p-card data-variant="flat">
          <!-- Balance Display Section -->
          <div *ngIf="lotBalance" class="mb-6">
            <p-card [style]="{ background: '#f0f9ff', border: '1px solid #0ea5e9' }">
              <div class="grid-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="font-semibold text-gray-700">{{ 'Total_Balance' | translateWF: 'Total Balance' }}:</label>
                  <p class="text-xl font-bold text-blue-600">{{ lotBalance.totalBalance | number: '1.2-2' }} {{ lotBalance.uom }}</p>
                </div>
                <div>
                  <label class="font-semibold text-gray-700">{{ 'Lot_Items_Count' | translateWF: 'Lot Items Count' }}:</label>
                  <p class="text-xl font-bold text-gray-800">{{ lotBalance.lotItemCount }}</p>
                </div>
                <div>
                  <label class="font-semibold text-gray-700">{{ 'Item_Codes' | translateWF: 'Item Codes' }}:</label>
                  <p class="text-sm text-gray-600">{{ lotBalance.itemCodes ? lotBalance.itemCodes.join(', ') : '' }}</p>
                </div>
              </div>
            </p-card>
          </div>

          <div class="grid-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div class="form-box">
              <label for="providerLotId">{{ 'Provider_Lot_ID' | translateWF: 'Provider Lot ID' }} <span class="text-red-500">*</span></label>
              <input pInputText id="providerLotId" formControlName="providerLotId" [placeholder]="'Enter_Provider_Lot_ID' | translateWF: 'Enter provider lot ID'" class="w-full" />
              <small class="text-gray-500">{{
                'Provider_Lot_ID_Help'
                  | translateWF
                    : 'The provider lot ID to reduce
                inventory from'
              }}</small>
            </div>

            <div class="form-box">
              <label for="reductionAmount">{{ 'Reduction_Amount' | translateWF: 'Reduction Amount' }} <span class="text-red-500">*</span></label>
              <p-inputNumber
                inputId="reductionAmount"
                formControlName="reductionAmount"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                placeholder="0.00"
                class="w-full"
              ></p-inputNumber>
              <small class="text-gray-500">{{
                'Reduction_Amount_Help'
                  | translateWF
                    : 'Amount to reduce from the lot
                (uses FIFO)'
              }}</small>
            </div>

            <div class="form-box">
              <label for="locationCode">{{ 'Source_Location' | translateWF: 'Source Location' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="locationCode"
                formControlName="locationCode"
                [options]="sourceLocationOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_Source_Location' | translateWF: 'Select Source Location'"
                class="w-full"
              ></p-select>
              <small class="text-gray-500">{{
                'Source_Location_Help'
                  | translateWF
                    : 'Source warehouse location for the
                lot reduction'
              }}</small>
            </div>

            <div class="form-box">
              <label for="uom">{{ 'Unit_Of_Measure' | translateWF: 'Unit of Measure' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="uom"
                formControlName="uom"
                [options]="uomOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_UOM' | translateWF: 'Select UOM'"
                class="w-full"
              ></p-select>
            </div>

            <div class="form-box lg:span-2">
              <label for="reason">{{ 'Common_Reason' | translateWF: 'Reason' }} <span class="text-red-500">*</span></label>
              <input pInputText id="reason" formControlName="reason" [placeholder]="'Enter_Reason_Reduction' | translateWF: 'Enter reason for reduction'" class="w-full" />
              <small class="text-gray-500">{{
                'Reason_Reduction_Help'
                  | translateWF
                    : 'Reason for the inventory
                reduction'
              }}</small>
            </div>

            <div class="form-box lg:span-3">
              <label for="comments">{{ 'Common_Comments' | translateWF: 'Comments' }}</label>
              <textarea
                pInputTextarea
                id="comments"
                formControlName="comments"
                rows="3"
                [placeholder]="'Optional_Comments' | translateWF: 'Optional comments...'"
                class="w-full"
              ></textarea>
            </div>
          </div>
        </p-card>
      </p-panel>
    </form>
  </div>

  <!-- LOT ITEM CREATE MODE -->
  <div class="content col" *ngIf="isCreateMode && isLotItemMode">
    <form [formGroup]="lotItemForm" class="col">
      <p-panel [header]="'Lot_Item_Information' | translateWF: 'Lot Item Information'">
        <p-card data-variant="flat">
          <div class="grid-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div class="form-box">
              <label for="providerLotId">{{ 'Provider_Lot_ID' | translateWF: 'Provider Lot ID' }} <span class="text-red-500">*</span></label>
              <input pInputText id="providerLotId" formControlName="providerLotId" [placeholder]="'Enter_Provider_Lot_ID' | translateWF: 'Enter provider lot ID'" class="w-full" />
            </div>

            <div class="form-box">
              <label for="qty">{{ 'Common_Quantity' | translateWF: 'Quantity' }} <span class="text-red-500">*</span></label>
              <p-inputNumber inputId="qty" formControlName="qty" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" placeholder="0.00" class="w-full"></p-inputNumber>
            </div>

            <div class="form-box">
              <label for="itemCode">{{ 'Common_Item_Code' | translateWF: 'Item Code' }} <span class="text-red-500">*</span></label>
              <input pInputText id="itemCode" formControlName="itemCode" [placeholder]="'Enter_Item_Code' | translateWF: 'Enter item code'" class="w-full" />
            </div>

            <div class="form-box">
              <label for="manufacturerReference">{{ 'Manufacturer_Reference' | translateWF: 'Manufacturer Reference' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="manufacturerReference"
                formControlName="manufacturerReference"
                [options]="manufacturerOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_Manufacturer' | translateWF: 'Select manufacturer'"
                class="w-full"
                [filter]="true"
                [filterPlaceholder]="'Search_Manufacturers' | translateWF: 'Search manufacturers...'"
              ></p-select>
            </div>

            <div class="form-box">
              <label for="currentLocationCode">{{ 'Destination_Location' | translateWF: 'Destination Location' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="currentLocationCode"
                formControlName="currentLocationCode"
                [options]="sourceLocationOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_Destination_Location' | translateWF: 'Select Destination Location'"
                class="w-full"
              ></p-select>
              <small class="text-gray-500">{{
                'Destination_Location_Help'
                  | translateWF
                    : 'Warehouse location where the
                lot item will be stored'
              }}</small>
            </div>

            <div class="form-box">
              <label for="packagingType">{{ 'Packaging_Type' | translateWF: 'Packaging Type' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="packagingType"
                formControlName="packagingType"
                [options]="[
                  { label: 'Box', value: 1 },
                  { label: 'Pallet', value: 2 },
                  { label: 'Bag', value: 3 },
                  { label: 'Roll', value: 4 },
                  { label: 'Other', value: 5 },
                ]"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_Packaging_Type' | translateWF: 'Select packaging type'"
                class="w-full"
              ></p-select>
            </div>

            <div class="form-box lg:span-3">
              <label for="notes">{{ 'Common_Notes' | translateWF: 'Notes' }}</label>
              <textarea pInputTextarea id="notes" formControlName="notes" rows="3" [placeholder]="'Optional_Notes' | translateWF: 'Optional notes...'" class="w-full"></textarea>
            </div>
          </div>
        </p-card>
      </p-panel>
    </form>
  </div>

  <!-- STANDARD ADJUSTMENT CREATE MODE -->
  <div class="content col" *ngIf="isCreateMode && !isLotItemMode && !isLotReductionMode">
    <form [formGroup]="adjustmentForm" class="col">
      <!-- Adjustment Information -->
      <p-panel [header]="'Adjustment_Information' | translateWF: 'Adjustment Information'">
        <p-card data-variant="flat">
          <div class="grid-1 md:grid-cols-2 gap-4">
            <div class="form-box">
              <label for="type">{{ 'Adjustment_Type' | translateWF: 'Adjustment Type' }} <span class="text-red-500">*</span></label>
              <p-select
                inputId="type"
                formControlName="type"
                [options]="typeOptions"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'Select_Adjustment_Type' | translateWF: 'Select adjustment type'"
                class="w-full"
              ></p-select>
              <small class="text-gray-500">{{
                'Adjustment_Type_Help'
                  | translateWF
                    : 'Select the type of adjustment you
                want to make'
              }}</small>
            </div>

            <div class="form-box">
              <label for="reason">{{ 'Common_Reason' | translateWF: 'Reason' }} <span class="text-red-500">*</span></label>
              <input pInputText id="reason" formControlName="reason" [placeholder]="'Enter_Reason_Adjustment' | translateWF: 'Enter reason for adjustment'" class="w-full" />
              <small class="text-gray-500">{{
                'Reason_Adjustment_Help'
                  | translateWF
                    : 'Provide a brief reason for this
                adjustment'
              }}</small>
            </div>
          </div>
        </p-card>
      </p-panel>

      <!-- Adjustment Lines -->
      <p-panel [header]="('Adjustment_Lines' | translateWF: 'Adjustment Lines') + ' (' + lines.length + ')'">
        <ng-template pTemplate="icons">
          <p-button [label]="'Add_Line' | translateWF: 'Add Line'" icon="pi pi-plus" size="small" (click)="addLine()"></p-button>
        </ng-template>
        <div class="col-sm">
          <!-- Lines -->
          <p-panel header="Line {{ i + 1 }}" *ngFor="let line of lines; let i = index" data-variant="surface">
            <ng-template pTemplate="icons">
              <p-button *ngIf="lines.length > 1" icon="pi pi-trash" [text]="true" severity="danger" size="small" (click)="removeLine(i)"></p-button>
            </ng-template>

            <!-- Line Content -->
            <p-card>
              <div class="col">
                <div class="grid-auto-3">
                  <!-- Lot Item Number - Exact Search -->
                  <div class="form-box">
                    <label>{{ 'Lot_Item_Number' | translateWF: 'Lot Item Number' }} <span class="text-red-500">*</span></label>
                    <div class="row-sm">
                      <input
                        pInputText
                        [(ngModel)]="line.lotItemNumber"
                        [ngModelOptions]="{ standalone: true }"
                        [placeholder]="'Enter_Lot_Item_Number_Placeholder' | translateWF: 'Enter exact lot item number (e.g., LI-1234)'"
                        class="flex-1"
                        (keyup.enter)="searchLotItemByNumber(line)"
                      />
                      <p-button
                        icon="pi pi-search"
                        [loading]="isSearching"
                        (click)="searchLotItemByNumber(line)"
                        [pTooltip]="'Search_Lot_Item' | translateWF: 'Search for lot item'"
                      ></p-button>
                    </div>
                    <small class="text-gray-500">{{
                      'Lot_Item_Number_Help'
                        | translateWF
                          : 'Enter the exact lot item
                      number
                      and click search'
                    }}</small>
                  </div>

                  <!-- Location Fields (shown based on type) -->
                  <div class="form-box" *ngIf="showLocationFields">
                    <label>{{ 'From_Location' | translateWF: 'From Location' }}</label>
                    <p-select
                      [(ngModel)]="line.fromLocationCode"
                      [ngModelOptions]="{ standalone: true }"
                      [options]="sourceLocationOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'Select_Source_Location' | translateWF: 'Select Source Location'"
                      class="w-full"
                      [disabled]="true"
                    ></p-select>
                  </div>

                  <div class="form-box" *ngIf="showLocationFields">
                    <label
                      >{{ 'To_Location' | translateWF: 'To Location' }}
                      <span class="text-red-500" *ngIf="selectedType === AdjustmentType.LocationTransfer || selectedType === AdjustmentType.LocationAndQuantity">*</span></label
                    >
                    <p-select
                      [(ngModel)]="line.toLocationCode"
                      [ngModelOptions]="{ standalone: true }"
                      [options]="sourceLocationOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'Select_Destination_Location' | translateWF: 'Select Destination Location'"
                      class="w-full"
                    ></p-select>
                  </div>

                  <!-- Quantity Fields (shown based on type) -->
                  <div class="form-box" *ngIf="showQuantityFields">
                    <label>{{ 'Current_Qty' | translateWF: 'Current Qty' }}</label>
                    <input pInputText [value]="line.lotItem?.netQuantity || 0" [disabled]="true" class="w-full" />
                  </div>

                  <div class="form-box" *ngIf="showQuantityFields">
                    <label
                      >{{ 'Qty_Adjustment' | translateWF: 'Qty Adjustment' }}
                      <span class="text-red-500" *ngIf="selectedType === AdjustmentType.QuantityCorrection || selectedType === AdjustmentType.LocationAndQuantity">*</span></label
                    >
                    <p-inputNumber
                      [(ngModel)]="line.quantityAdjustment"
                      [ngModelOptions]="{ standalone: true }"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      placeholder="0.00"
                      class="w-full"
                    ></p-inputNumber>
                    <small class="text-gray-500">{{
                      'Qty_Adjustment_Help'
                        | translateWF
                          : 'Use negative values to reduce
                      quantity'
                    }}</small>
                  </div>

                  <div class="form-box" *ngIf="showQuantityFields">
                    <label>{{ 'New_Quantity' | translateWF: 'New Quantity' }}</label>
                    <input pInputText [value]="calculateNewQuantity(line)" [disabled]="true" class="w-full" />
                  </div>

                  <!-- UOM -->
                  <div class="form-box">
                    <label>{{ 'UOM' | translateWF: 'UOM' }}</label>
                    <p-select
                      [(ngModel)]="line.uom"
                      [ngModelOptions]="{ standalone: true }"
                      [options]="uomOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'Select_UOM' | translateWF: 'Select UOM'"
                      class="w-full"
                    ></p-select>
                  </div>

                  <!-- Lot Item Details (if selected) -->
                  <div class="lg:span-3 bg-blue-50 p-3 rounded" *ngIf="line.lotItem">
                    <div class="grid-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span class="font-semibold">{{ 'Common_Item_Code' | translateWF: 'Item Code' }}:</span> {{ line.lotItem.itemCode }}
                      </div>
                      <div>
                        <span class="font-semibold">{{ 'Current_Qty' | translateWF: 'Current Qty' }}:</span> {{ line.lotItem.netQuantity }}
                      </div>
                      <div>
                        <span class="font-semibold">{{ 'Location' | translateWF: 'Location' }}:</span> {{ line.lotItem.currentLocationCode }}
                      </div>
                      <div>
                        <span class="font-semibold">{{ 'Gross_Qty' | translateWF: 'Gross Qty' }}:</span> {{ line.lotItem.grossQuantity }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Comments -->
                <div class="form-box">
                  <label>{{ 'Common_Comments' | translateWF: 'Comments' }}</label>
                  <textarea
                    pInputTextarea
                    [(ngModel)]="line.comments"
                    [ngModelOptions]="{ standalone: true }"
                    rows="2"
                    [placeholder]="'Line_Comments_Placeholder' | translateWF: 'Optional comments for this line...'"
                    class="w-full"
                  ></textarea>
                </div>
              </div>
            </p-card>
          </p-panel>
        </div>
      </p-panel>
    </form>
  </div>

  <!-- VIEW MODE CONTENT -->
  <div class="content col" *ngIf="!isCreateMode && !isLoading() && adjustment">
    <!-- Summary Information -->
    <p-panel [header]="'Adjustment_Information' | translateWF: 'Adjustment Information'">
      <p-card data-variant="flat">
        <div class="col">
          <div class="grid-auto-4">
            <div class="form-box">
              <label>{{ 'Common_Status' | translateWF: 'Status' }}</label>
              <div>
                <p-tag [value]="getStatusLabel(adjustment.status)" [severity]="getStatusSeverity(adjustment.status)"> </p-tag>
              </div>
            </div>

            <div class="form-box">
              <label>{{ 'Adjustment_Type' | translateWF: 'Type' }}</label>
              <p>{{ getTypeLabel(adjustment.type) }}</p>
            </div>

            <div class="form-box">
              <label>{{ 'Created_Date' | translateWF: 'Created Date' }}</label>
              <p>{{ formatDate(adjustment.createdDate) }}</p>
            </div>

            <div class="form-box">
              <label>{{ 'Created_By' | translateWF: 'Created By' }}</label>
              <p>{{ adjustment.createdBy || '-' }}</p>
            </div>
          </div>
          <div class="form-box">
            <label>{{ 'Common_Reason' | translateWF: 'Reason' }}</label>
            <p>{{ adjustment.reason }}</p>
          </div>
        </div>
      </p-card>
    </p-panel>

    <!-- Adjustment Lines -->
    <p-panel [header]="('Adjustment_Lines' | translateWF: 'Adjustment Lines') + ' (' + (adjustment.lines?.length ?? 0) + ')'">
      <p-card data-variant="compact">
        <p-table [value]="adjustment.lines || []" [tableStyle]="{ 'min-width': '60rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'Lot_Item_Number' | translateWF: 'Lot Item Number' }}</th>
              <th>{{ 'From_Location' | translateWF: 'From Location' }}</th>
              <th>{{ 'To_Location' | translateWF: 'To Location' }}</th>
              <th class="text-right">{{ 'Qty_Adjustment' | translateWF: 'Quantity Adjustment' }}</th>
              <th>{{ 'UOM' | translateWF: 'UOM' }}</th>
              <th>{{ 'Common_Comments' | translateWF: 'Comments' }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-line>
            <tr>
              <td class="font-semibold">{{ line.lotItemNumber }}</td>
              <td>{{ line.fromLocationCode || '-' }}</td>
              <td>{{ line.toLocationCode || '-' }}</td>
              <td class="text-right">
                <span
                  *ngIf="line.quantityAdjustment !== null && line.quantityAdjustment !== undefined"
                  [ngClass]="{
                    'text-green-600 font-semibold': line.quantityAdjustment > 0,
                    'text-red-600 font-semibold': line.quantityAdjustment < 0,
                  }"
                >
                  {{ line.quantityAdjustment > 0 ? '+' : '' }}{{ line.quantityAdjustment | number: '1.2-2' }}
                </span>
                <span *ngIf="line.quantityAdjustment === null || line.quantityAdjustment === undefined">-</span>
              </td>
              <td>{{ line.uom }}</td>
              <td>{{ line.comments || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center py-4">
                {{
                  'No_Adjustment_Lines'
                    | translateWF
                      : 'No adjustment lines
                found'
                }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </p-panel>

    <!-- Notes -->
    <p-panel [header]="'Common_Notes' | translateWF: 'Notes'" *ngIf="adjustment.notes && adjustment.notes.length > 0">
      <p-card data-variant="flat">
        <div *ngFor="let note of adjustment.notes" class="border-b last:border-b-0 py-3">
          <div class="row-between-stretch items-start mb-2">
            <span class="font-semibold">{{ note.createdBy || 'Unknown' }}</span>
            <span class="text-sm text-gray-600">{{ formatDate(note.createdDate) }}</span>
          </div>
          <p class="text-gray-700">{{ note.content }}</p>
        </div>
      </p-card>
    </p-panel>
  </div>
</main>

<p-confirmdialog />
